// Orval mutator (the HTTP transport the generated client calls through).
// Referenced by `orval.config.ts`'s `override.mutator = { path: 'src/http.ts',
// name: 'http' }` — every generated `@tanstack/vue-query` composable routes
// its request through this one `http(...)` function instead of importing
// axios directly, so base URL / auth header / error normalisation are
// configured in ONE place the app controls rather than scattered across
// generated code.
//
// This is codegen harness glue (transport wiring), NOT application feature
// logic: it holds no endpoint/DTO knowledge (those are generated from the
// OpenAPI document) — it only owns "how a request is sent". `apps/web`
// customises the shared instance (base URL, bearer token, refresh) via
// `setApiClientConfig` at bootstrap; kept dependency-light so the generated
// client stays reproducible under the CI drift gate.
import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';

/** No response within this window rejects the request rather than hanging
 * forever (BIO-silent-rename-loss-on-stalled-request). Root cause: this
 * instance previously had NO timeout (axios' own default is `0`, i.e.
 * infinite) — when a request is sent but its response never arrives (server
 * restart mid-request, a dropped connection, an edge/proxy that swallows the
 * response without closing the socket), the awaited mutation promise never
 * resolves OR rejects. Every caller that gates user feedback on that await
 * (e.g. `SubjectBuilderView.vue`'s `run()` — success toast on resolve, error
 * toast on reject) then does neither: no error, no success, and whatever
 * optimistic/placeholder UI state was showing (e.g. a chapter's "New
 * Chapter" placeholder title) is left stuck indefinitely, looking like a
 * silently dropped write. Confirmed via three independent captured network
 * traces (two from QA, one reproduced live): a PATCH request was
 * demonstrably SENT — the guard/comparison logic and the rename input's DOM
 * focus were both observed correct at the moment of commit in every case —
 * but its response was never recorded (HAR `status: -1`), matching this
 * app's local dev harness's documented cumulative workerd-instability
 * failure mode, and mirroring how a real dropped connection would behave in
 * production. A bounded timeout turns that silent hang into a real rejected
 * promise, which `problemMessage()`/`toUserMessage()` already render as
 * "Cannot reach the server. Check your connection and try again." — the
 * defect was the missing timeout, not any UI orchestration around it. */
export const DEFAULT_REQUEST_TIMEOUT_MS = 20_000;

/** The single axios instance every generated composable shares. Created with
 * no baseURL/interceptors (beyond the default timeout above) so this package
 * is import-safe in any context (SSR/test/browser); `apps/web` configures
 * baseURL/auth at startup. */
export const apiClientInstance: AxiosInstance = axios.create({
  timeout: DEFAULT_REQUEST_TIMEOUT_MS,
});

/** Id of the currently-registered auth request interceptor, if any — tracked
 * at module scope so repeat `configureApiClient` calls (app boot re-run under
 * Vite HMR, tests, etc.) can eject the previous handler before installing a
 * fresh one instead of stacking duplicate interceptors on the shared axios
 * instance. */
let authInterceptorId: number | null = null;

/** Consumer hook (called once from `apps/web`'s bootstrap, and safely
 * re-callable e.g. across Vite HMR reloads) to point the shared instance at
 * the deployed API and attach the bearer token — kept here so generated code
 * never needs editing to wire runtime config.
 *
 * Idempotent: calling this multiple times always leaves exactly ONE auth
 * request interceptor registered on `apiClientInstance`, reflecting the most
 * recent config — a prior handler (if any) is ejected before the new one is
 * installed rather than left stacked. */
export function configureApiClient(config: {
  baseURL?: string;
  getAccessToken?: () => string | null | undefined;
}): void {
  if (config.baseURL !== undefined) {
    apiClientInstance.defaults.baseURL = config.baseURL;
  }
  if (authInterceptorId !== null) {
    apiClientInstance.interceptors.request.eject(authInterceptorId);
    authInterceptorId = null;
  }
  if (config.getAccessToken) {
    const getToken = config.getAccessToken;
    authInterceptorId = apiClientInstance.interceptors.request.use((req) => {
      const token = getToken();
      if (token) {
        req.headers.set('Authorization', `Bearer ${token}`);
      }
      return req;
    });
  }
}

/** Orval-invoked mutator. Returns the response `data` (unwrapped) and exposes
 * a `.cancel()` on the returned promise, the shape orval's vue-query client
 * expects. */
export const http = <T>(config: AxiosRequestConfig, options?: AxiosRequestConfig): Promise<T> => {
  const source = axios.CancelToken.source();
  const promise = apiClientInstance({
    ...config,
    ...options,
    cancelToken: source.token,
  }).then(({ data }: AxiosResponse<T>) => data);

  // orval's generated `queryFn` reads this to cancel in-flight requests.
  (promise as Promise<T> & { cancel?: () => void }).cancel = () => {
    source.cancel('Query was cancelled');
  };

  return promise;
};

export default http;
